import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Filter, Sparkles, Check, X as XIcon } from "lucide-react";
import { api, formatError } from "../lib/api";
import AppShell from "../components/AppShell";

const STATUSES = ["new", "triaged", "planned", "in_progress", "resolved", "closed"];
const CATEGORIES = ["bug", "feature_request", "content", "usability", "billing", "other"];

/**
 * Super-admin feedback triage panel.
 * Attachments are streamed through the authenticated route
 * `/api/feedback/{id}/attachments/{aid}` — no public directory.
 */
export default function AdminFeedbackPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState(null);
  const [triaging, setTriaging] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    setLoadingList(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (category) params.category = category;
      const r = await api.get("/admin/feedback", { params });
      setItems(r.data?.items || []);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { load(); }, [status, category]);

  const openDetail = async (item) => {
    setSelected(item);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const r = await api.get(`/admin/feedback/${item.id}`);
      setDetail(r.data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoadingDetail(false);
    }
  };

  const updateStatus = async (nextStatus) => {
    if (!selected) return;
    setSavingStatus(true);
    try {
      const r = await api.patch(`/admin/feedback/${selected.id}`, { status: nextStatus });
      setDetail((prev) => (prev ? { ...prev, item: r.data } : prev));
      // Update in list
      setItems((prev) => prev.map((i) => (i.id === selected.id ? r.data : i)));
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const runTriage = async () => {
    if (!selected) return;
    setTriaging(true);
    try {
      const r = await api.post(`/admin/feedback/${selected.id}/ai-triage`);
      setDetail((prev) => (prev ? { ...prev, item: r.data } : prev));
    } catch (err) {
      setError(formatError(err));
    } finally {
      setTriaging(false);
    }
  };

  const attachmentUrl = (att) => {
    const bu = process.env.REACT_APP_BACKEND_URL || "";
    return `${bu}/api/feedback/${selected.id}/attachments/${att.id}`;
  };

  return (
    <AppShell title="Admin · Feedback">
      <div className="w-full py-4 md:py-6" data-testid="admin-feedback-page">
        <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">Feedback triage</h1>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-neutral-500" />
          <select data-testid="admin-fb-filter-status"
            className="text-sm rounded-md border border-neutral-300 px-2 py-1.5"
            value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="admin-fb-filter-category"
            className="text-sm rounded-md border border-neutral-300 px-2 py-1.5"
            value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {error && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>
        )}

        <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
          {/* List */}
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <div className="px-3 py-2 border-b border-neutral-200 text-xs uppercase font-semibold text-neutral-500">
              {items.length} item{items.length === 1 ? "" : "s"}
            </div>
            {loadingList ? (
              <div className="p-4 text-sm text-neutral-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="p-6 text-sm text-neutral-500 text-center" data-testid="admin-fb-empty">No feedback matches these filters.</div>
            ) : (
              <ul className="divide-y divide-neutral-100 max-h-[70vh] overflow-y-auto">
                {items.map((i) => (
                  <li key={i.id}>
                    <button
                      data-testid={`admin-fb-row-${i.id}`}
                      onClick={() => openDetail(i)}
                      className={`w-full text-left px-3 py-2 hover:bg-neutral-50 ${selected?.id === i.id ? "bg-neutral-100" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-neutral-900 truncate">{i.title}</div>
                        <StatusPill status={i.status} />
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        {i.category} · {new Date(i.created_at).toLocaleDateString()}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail */}
          <div className="rounded-lg border border-neutral-200 bg-white">
            {!selected && (
              <div className="p-8 text-sm text-neutral-500 text-center">Select a feedback item to review.</div>
            )}
            {selected && loadingDetail && (
              <div className="p-6 text-sm text-neutral-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading detail…
              </div>
            )}
            {selected && detail && (
              <div className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">{detail.item.title}</h2>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {detail.item.category} · from {detail.item.submitter_name || "Unknown"} ({detail.item.submitter_email || "no email"})
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      account <code className="font-mono">{detail.item.account_id}</code>
                    </div>
                  </div>
                  <StatusPill status={detail.item.status} />
                </div>

                <div className="text-sm text-neutral-800 whitespace-pre-wrap bg-neutral-50 border border-neutral-200 rounded p-3">
                  {detail.item.body}
                </div>

                {/* Attachments */}
                {detail.attachments?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase font-semibold text-neutral-500 mb-1">Screenshots</div>
                    <div className="flex flex-wrap gap-2">
                      {detail.attachments.map((a) => (
                        <a key={a.id}
                          data-testid={`admin-fb-attachment-${a.id}`}
                          href={attachmentUrl(a)} target="_blank" rel="noreferrer"
                          className="block border border-neutral-200 rounded overflow-hidden hover:ring-2 hover:ring-neutral-800"
                        >
                          <img src={attachmentUrl(a)} alt={a.display_name}
                            className="h-24 w-24 object-cover bg-neutral-100" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI triage */}
                <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
                      <Sparkles className="h-4 w-4" /> AI triage <span className="text-[10px] uppercase text-neutral-500 font-normal">proposal only</span>
                    </div>
                    <button
                      data-testid="admin-fb-run-triage"
                      disabled={triaging}
                      onClick={runTriage}
                      className="text-xs px-2 py-1 rounded border border-neutral-300 bg-white hover:bg-neutral-100 disabled:opacity-40"
                    >{triaging ? "Working…" : "Run AI triage"}</button>
                  </div>
                  {detail.item.ai_triage ? (
                    <>
                      {detail.item.ai_triage.error ? (
                        <div className="text-xs text-red-700">Provider error: {detail.item.ai_triage.error}</div>
                      ) : (
                        <pre className="text-[12px] whitespace-pre-wrap font-mono text-neutral-800">{detail.item.ai_triage.raw}</pre>
                      )}
                      <div className="text-[10px] text-neutral-500 mt-1">
                        {detail.item.ai_triage.provider}/{detail.item.ai_triage.model}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-neutral-500">No triage yet. Human judgment always overrides AI proposals.</div>
                  )}
                </div>

                {/* Status */}
                <div>
                  <div className="text-xs uppercase font-semibold text-neutral-500 mb-1">Set status</div>
                  <div className="flex flex-wrap gap-1">
                    {STATUSES.map((s) => (
                      <button key={s}
                        data-testid={`admin-fb-status-${s}`}
                        onClick={() => updateStatus(s)}
                        disabled={savingStatus || detail.item.status === s}
                        className={`text-xs px-2 py-1 rounded border ${detail.item.status === s ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 bg-white hover:bg-neutral-100"} disabled:opacity-60`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}


function StatusPill({ status }) {
  const map = {
    new: "bg-sky-100 text-sky-800",
    triaged: "bg-violet-100 text-violet-800",
    planned: "bg-pink-100 text-pink-800",
    in_progress: "bg-yellow-100 text-yellow-800",
    resolved: "bg-emerald-100 text-emerald-800",
    closed: "bg-neutral-200 text-neutral-700",
  };
  return <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${map[status] || "bg-neutral-100"}`}>{status}</span>;
}
